import { useEffect, useState } from "react";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import FaqAccordion from "@/components/feature/FaqAccordion";
import CtaBand from "@/components/feature/CtaBand";
import { useSeo } from "@/hooks/useSeo";
import { generalFaqs } from "@/mocks/faq";
import { guideSections, headlineExamples, submissionMaterials } from "@/mocks/guide";
import GuideToc from "./components/GuideToc";

const tocItems = guideSections.map((s) => ({ id: s.id, no: s.no, title: s.title }));

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "投稿指南：爆款稿件特征、标题技巧与编辑偏好方向",
    description:
      "系统讲解高热度选题的判断标准、爆款稿件共同特征、投稿标题技巧与编辑偏好方向，并附投稿前自查清单。",
    author: { "@type": "Organization", name: "爆点投稿社" },
    publisher: { "@type": "Organization", name: "爆点投稿社" },
    mainEntityOfPage: "https://sxnxp.cn/guide",
    datePublished: "2026-09-01",
    dateModified: "2026-09-10",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://sxnxp.cn/" },
      { "@type": "ListItem", position: 2, name: "投稿指南", item: "https://sxnxp.cn/guide" },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: generalFaqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  },
];

export default function Guide() {
  const [progress, setProgress] = useState(0);

  useSeo({
    title: "投稿指南 - 爆款稿件特征与投稿标题技巧 | 爆点投稿社",
    description:
      "从高热度选题判断到爆款稿件特征、投稿标题技巧与编辑偏好方向，一篇讲清楚怎么写出容易被采用的爆料投稿，附投稿前自查清单。",
    keywords: "爆款稿件特征,投稿标题技巧,编辑偏好方向,爆料投稿,选题参考",
    path: "/guide",
    jsonLd,
  });

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      setProgress(total > 0 ? Math.min(100, (window.scrollY / total) * 100) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <div className="sticky top-16 z-40 h-0.5 w-full bg-background-200 md:top-[72px]">
        <div className="h-full bg-primary-500 transition-[width] duration-150" style={{ width: `${progress}%` }}></div>
      </div>

      <section className="w-full border-b border-background-200 bg-background-100 px-4 pb-10 pt-8 md:px-6 md:pb-14 md:pt-10">
        <div className="mx-auto w-full">
          <Breadcrumbs items={[{ label: "投稿指南" }]} />
          <div className="mt-6 max-w-3xl">
            <p className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.2em] uppercase text-primary-600">
              <span className="h-px w-6 bg-primary-400"></span>
              过稿说明书
            </p>
            <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
              投稿指南：爆款稿件特征、标题技巧与编辑偏好方向
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              这是一份写给新手的投稿说明书。它不教你写作文，只回答三个最实际的问题：
              什么样的选题值得投、爆款稿件到底强在哪里、编辑真正在意的是什么。
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-sm leading-none"></i>
                全文约 12 分钟
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-refresh-line text-sm leading-none"></i>
                最近更新 2026-09-10
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-article-line text-sm leading-none"></i>
                共 6 个章节
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 px-4 py-12 md:px-6 md:py-16">
        <div className="mx-auto grid w-full grid-cols-1 gap-12 lg:grid-cols-12">
          <aside className="lg:col-span-3">
            <GuideToc items={tocItems} />
          </aside>

          <div className="lg:col-span-9">
            <div className="space-y-14">
              {guideSections.map((section) => (
                <article key={section.id} id={section.id} className="scroll-mt-24">
                  <div className="flex items-baseline gap-4">
                    <span className="font-heading text-3xl font-bold text-primary-500">{section.no}</span>
                    <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                      {section.title}
                    </h2>
                  </div>
                  <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
                    {section.intro}
                  </p>

                  <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {section.points.map((point) => (
                      <div
                        key={point.label}
                        className="rounded-lg border border-background-200 bg-background-100/60 p-4 md:p-5"
                      >
                        <p className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950 md:text-base">
                          <i className="ri-checkbox-circle-fill text-base leading-none text-secondary-500"></i>
                          {point.label}
                        </p>
                        <p className="mt-2 text-sm leading-relaxed text-foreground-600">{point.desc}</p>
                      </div>
                    ))}
                  </div>

                  {section.id === "headline" && (
                    <div className="mt-8 overflow-hidden rounded-lg border border-background-200">
                      <div className="flex items-center gap-2 bg-foreground-950 px-5 py-3.5 text-background-50">
                        <i className="ri-magic-line text-base leading-none text-accent-400"></i>
                        <p className="text-sm font-medium">标题改写对照表</p>
                      </div>
                      <div className="divide-y divide-background-200 bg-background-50">
                        {headlineExamples.map((example) => (
                          <div key={example.after} className="grid grid-cols-1 gap-3 p-5 md:grid-cols-2 md:gap-6">
                            <div>
                              <p className="text-[11px] font-medium text-foreground-500">原稿标题</p>
                              <p className="mt-1.5 text-sm leading-snug text-foreground-500 line-through decoration-foreground-400">
                                {example.before}
                              </p>
                            </div>
                            <div>
                              <p className="text-[11px] font-medium text-primary-600">改写后</p>
                              <p className="mt-1.5 font-heading text-base font-semibold leading-snug text-foreground-950">
                                {example.after}
                              </p>
                              <p className="mt-2 text-xs leading-relaxed text-foreground-500">
                                {example.reason}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {section.id === "checklist" && (
                    <div className="mt-8 rounded-lg bg-secondary-900 p-6 md:p-8">
                      <p className="text-xs font-medium tracking-[0.2em] uppercase text-accent-400">
                        投稿前，最好手上有的四样东西
                      </p>
                      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                        {submissionMaterials.map((material) => (
                          <div key={material.title} className="flex gap-4">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50/10 text-accent-400">
                              <i className={`${material.icon} text-lg leading-none`}></i>
                            </span>
                            <div>
                              <p className="font-heading text-sm font-semibold text-background-50">
                                {material.title}
                              </p>
                              <p className="mt-1.5 text-xs leading-relaxed text-background-100/70">
                                {material.desc}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="guide-faq" className="w-full scroll-mt-20 bg-background-100 px-4 py-16 md:px-6 md:py-20">
        <div className="mx-auto grid w-full grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.2em] uppercase text-primary-600">
              <span className="h-px w-6 bg-primary-400"></span>
              常见问题
            </p>
            <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              关于投稿流程，还有这些疑问
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              这些问题来自真实投稿者的提问。读完之后如果还有不确定的地方，直接带着线索提交，编辑会一并解答。
            </p>
          </div>
          <div className="lg:col-span-8">
            <FaqAccordion items={generalFaqs} />
          </div>
        </div>
      </section>

      <CtaBand
        title="把这份指南用在你的第一条线索上"
        desc="看懂了就动手。挑一条选题，按清单补齐材料，剩下的交给编辑。"
        secondaryLabel="回到选题库"
        secondaryTo="/topics"
      />
    </>
  );
}